import { serverTimestamp } from 'firebase/firestore'
import { ApiService } from './api.service'
import { FirestoreService2 } from './firestore.service2'
import { use } from './utils'

/**
 * Timestamp helpers mirroring `dateUtils.ts`. All return Unix timestamps
 * in milliseconds (number) at midnight (00:00:00) local time, except
 * `nowTimestamp()` (current instant) and `endOfMonthTimestamp()` (23:59:59.999).
 */

/** Current instant as Unix timestamp in ms (same as `new Date().getTime()`). */
export function nowTimestamp(): number {
  return new Date().getTime()
}

/** Today at 00:00:00 local time as Unix timestamp in ms. */
export function todayTimestamp(): number {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** Tomorrow at 00:00:00 local time as Unix timestamp in ms. */
export function tomorrowTimestamp(): number {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + 1)
  return d.getTime()
}

/** Yesterday at 00:00:00 local time as Unix timestamp in ms. */
export function yesterdayTimestamp(): number {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - 1)
  return d.getTime()
}

/** First day of the given (or current) month at 00:00:00 local time as Unix timestamp in ms. */
export function firstDayOfMonthTimestamp(date: Date = new Date()): number {
  return new Date(date.getFullYear(), date.getMonth(), 1).getTime()
}

/** Last day of the given (or current) month at 00:00:00 local time as Unix timestamp in ms. */
export function lastDayOfMonthTimestamp(date: Date = new Date()): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime()
}

/** Alias for firstDayOfMonthTimestamp. Returns Unix timestamp in ms. */
export function startOfMonthTimestamp(date: Date = new Date()): number {
  return firstDayOfMonthTimestamp(date)
}

/** Last day of the given (or current) month at 23:59:59.999 local time as Unix timestamp in ms. */
export function endOfMonthTimestamp(date: Date = new Date()): number {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  d.setHours(23, 59, 59, 999)
  return d.getTime()
}

/** Unix timestamp in ms for the given date at 00:00:00 local time. Returns NaN for invalid dates. */
export function toTimestamp(date: Date): number {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  return d.getTime()
}

/**
 * Timestamp parsing helpers for Firestore reads.
 *
 * Firestore returns dates in three shapes depending on the transport:
 *  - SDK (`onSnapshot`): a `Timestamp` class instance with `.toDate()` and
 *    `{ seconds, nanoseconds }`.
 *  - REST API (`firestore-parser`): a string in ISO 8601 ("2018-03-11T08:00:00Z").
 *  - Encrypted API (`listViaApi`): a plain `{ seconds, nanoseconds }` (or
 *    `{ _seconds, _nanoseconds }`) object produced by JSON serialization.
 *
 * Legacy documents may also store `createdAt`/`updatedAt` as ISO strings or
 * Unix timestamps (ms). These helpers normalize everything to a JS `Date`.
 */

/** Detect a Firestore Timestamp instance (SDK path). */
function isFirestoreTimestamp(value: any): boolean {
  return value != null
    && typeof value === 'object'
    && typeof value.toDate === 'function'
    && 'seconds' in value
    && 'nanoseconds' in value
}

/** Detect a serialized Timestamp (e.g. coming back from JSON via listViaApi). */
function isSerializedTimestamp(value: any): boolean {
  return value != null
    && typeof value === 'object'
    && !Array.isArray(value)
    && typeof value.toDate !== 'function'
    && ('seconds' in value || '_seconds' in value)
    && ('nanoseconds' in value || '_nanoseconds' in value)
}

/**
 * Detect a string that looks like a date: ISO 8601 (`YYYY-MM-DD`,
 * `YYYY-MM-DDTHH:mm:ss.sssZ`) or its `YYYY-MM-DD HH:mm` variant.
 * Strings that don't match the pattern are left untouched.
 */
function isDateLikeString(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?)?$/.test(value)
}

/**
 * Detect an integer that looks like a Unix timestamp: 10 digits (seconds,
 * range 2001-2100) or 13 digits (milliseconds, range 2001-2100). Plain
 * counters or IDs outside these ranges are ignored.
 */
function isTimestampNumber(value: number): boolean {
  if (!Number.isInteger(value)) return false
  if (value >= 978307200 && value <= 4102444800) return true   // seconds 2001-2100
  if (value >= 978307200000 && value <= 4102444800000) return true // ms 2001-2100
  return false
}

/**
 * Coerce any timestamp-like value to a JS `Date`, or `null` if it cannot be
 * interpreted as a date. Accepts Firestore `Timestamp`, serialized
 * `{ seconds, nanoseconds }`, ISO strings, and Unix timestamps in ms.
 */
export function coerceToDate(value: any): Date | null {
  // if (value == null) return null
  // if (value instanceof Date) return value
  if (isFirestoreTimestamp(value)) return value.toDate()
  if (isSerializedTimestamp(value)) {
    const seconds = value.seconds ?? value._seconds
    return new Date(seconds * 1000)
  }
  // if (typeof value === 'string') {
  //   const d = new Date(value)
  //   return isNaN(d.getTime()) ? null : d
  // }
  // if (typeof value === 'number') return new Date(value)
  // return null
  return value
}

/**
 * Recursively walk a Firestore document, converting every Firestore Timestamp
 * (SDK instance or serialized form) found at any depth into a JS `Date`.
 *
 * For the well-known timestamp fields (`createdAt`, `updatedAt`) it also
 * coerces legacy ISO strings and Unix timestamps, so consumers always receive
 * a `Date` regardless of how the field was originally stored.
 *
 * Mutates the document in place and returns it for convenience.
 */
export function parseDocTimestamps<T>(doc: T): T {
  if (!doc || typeof doc !== 'object' || doc instanceof Date) return doc
  const obj = doc as any

  for (const key of Object.keys(obj)) {
    const value = obj[key]

    if (isFirestoreTimestamp(value) || isSerializedTimestamp(value)) {
      obj[key] = coerceToDate(value)
    } else if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const item = value[i]
        if (isFirestoreTimestamp(item) || isSerializedTimestamp(item)) {
          value[i] = coerceToDate(item)
        } else if (item && typeof item === 'object' && !(item instanceof Date)) {
          parseDocTimestamps(item)
        }
      }
    } else if (value && typeof value === 'object' && !(value instanceof Date)) {
      parseDocTimestamps(value)
    }
  }

  // Legacy coercion for the well-known timestamp fields.
  for (const field of ['createdAt', 'updatedAt']) {
    if (field in obj && (typeof obj[field] === 'string' || typeof obj[field] === 'number')) {
      obj[field] = coerceToDate(obj[field])
    }
  }

  return doc
}

/**
 * Recursively walk a document and coerce legacy date values stored as
 * ISO strings (`YYYY-MM-DD`, `YYYY-MM-DDTHH:mm:ss.sssZ`) or Unix timestamps
 * (10-digit seconds or 13-digit ms) into JS `Date` objects, at any depth.
 *
 * Unlike `parseDocTimestamps`, which only handles Firestore `Timestamp`
 * instances and the well-known `createdAt`/`updatedAt` legacy fields, this
 * function targets plain string/number values that look like dates, so it
 * works on any field (e.g. `orderDate`, `beginDt`, `endDt`).
 *
 * Mutates the document in place and returns it for convenience. Intended to
 * be composed with `parseDocTimestamps` on documents coming from transports
 * that don't already normalize timestamps (e.g. `db/action` API responses):
 * `coerceLegacyDates(parseDocTimestamps(doc))`.
 */
export function coerceLegacyDates<T>(doc: T): T {
  if (!doc || typeof doc !== 'object' || doc instanceof Date) return doc
  const obj = doc as any

  for (const key of Object.keys(obj)) {
    const value = obj[key]

    if (typeof value === 'string' && isDateLikeString(value)) {
      obj[key] = coerceToDate(value)
    } else if (typeof value === 'number' && isTimestampNumber(value)) {
      obj[key] = coerceToDate(value)
    } else if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const item = value[i]
        if (typeof item === 'string' && isDateLikeString(item)) {
          value[i] = coerceToDate(item)
        } else if (typeof item === 'number' && isTimestampNumber(item)) {
          value[i] = coerceToDate(item)
        } else if (item && typeof item === 'object' && !(item instanceof Date)) {
          coerceLegacyDates(item)
        }
      }
    } else if (value && typeof value === 'object' && !(value instanceof Date)) {
      coerceLegacyDates(value)
    }
  }

  return doc
}

/**
 * Detect a FieldValue sentinel (e.g. `serverTimestamp()`, `increment()`).
 *
 * FieldValue instances have an `isEqual` method but lack the `seconds` /
 * `nanoseconds` / `toDate` combination that Timestamps have.
 */
function isFieldValueSentinel(value: any): boolean {
  return value != null
    && typeof value === 'object'
    && !Array.isArray(value)
    && !(value instanceof Date)
    && typeof value.isEqual === 'function'
    && !(typeof value.toDate === 'function' && 'seconds' in value && 'nanoseconds' in value)
}

/**
 * Detect a serialized (destroyed) `serverTimestamp()` sentinel — the plain
 * object `{ _methodName: "serverTimestamp" }` produced when a FieldValue
 * sentinel is passed through `JSON.stringify` / `JSON.parse`.
 */
function isDestroyedServerTimestamp(value: any): boolean {
  return value != null
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).length === 1
    && value._methodName === 'serverTimestamp'
}

/**
 * Deep clone a data object while preserving Firestore `FieldValue` sentinels
 * (such as the result of `serverTimestamp()`) and Firestore `Timestamp`
 * instances by reference.
 *
 * This is a drop-in replacement for `JSON.parse(JSON.stringify(data))` in
 * contexts (like `FirestoreService2.prepareData`) where the data may contain
 * `FieldValue` sentinels or `Timestamp` instances that must remain intact.
 * `JSON.stringify` would otherwise strip the sentinel metadata and leave a
 * plain `{ _methodName: "serverTimestamp" }` object that the Firestore SDK
 * no longer recognizes.
 *
 * As an extra safety net, if a value matching the destroyed-sentinel shape
 * `{ _methodName: "serverTimestamp" }` is encountered anywhere in the tree,
 * it is replaced with a fresh `serverTimestamp()` sentinel. This recovers
 * data that was serialized outside `FirestoreService2` before reaching here.
 *
 * Returns a new object/array for plain objects and arrays (deep cloned),
 * a fresh `Date` for `Date` instances, and the same reference for live
 * sentinels, `Timestamp` instances, primitives, `null` and `undefined`.
 */
export function deepClonePreservingFieldValues<T>(data: T): T {
  if (data == null) return data
  if (data instanceof Date) return new Date(data.getTime()) as T
  if (isDestroyedServerTimestamp(data)) return serverTimestamp() as T
  if (isFieldValueSentinel(data) || isFirestoreTimestamp(data)) return data
  if (Array.isArray(data)) return data.map(item => deepClonePreservingFieldValues(item)) as T
  if (typeof data === 'object') {
    const result: any = {}
    for (const key of Object.keys(data as any)) {
      if ((data as any)[key] === undefined) continue
      result[key] = deepClonePreservingFieldValues((data as any)[key])
    }
    return result as T
  }
  return data
}

/**
 * Recursively walk a data object (before sending to the API), converting
 * Firestore `Timestamp` instances and `FieldValue` sentinels (such as
 * `serverTimestamp()`) into plain `{ seconds, nanoseconds }` objects that
 * survive JSON serialization.
 *
 * The backend should then recognize `{ seconds, nanoseconds }` and recreate
 * a native Firestore Timestamp with those values.
 *
 * Returns a new object (does not mutate the original).
 */
export function serializeTimestampsForApi<T>(data: T): T {
  if (!data || typeof data !== 'object' || data instanceof Date) return data

  const obj = (Array.isArray(data) ? [...data] : { ...data }) as any

  for (const key of Object.keys(obj)) {
    const value = obj[key]
    if (value == null) continue

    if (isFirestoreTimestamp(value)) {
      obj[key] = { seconds: value.seconds, nanoseconds: value.nanoseconds }
    } else if (isFieldValueSentinel(value)) {
      obj[key] = { seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 }
    } else if (value instanceof Date) {
      obj[key] = { seconds: Math.floor(value.getTime() / 1000), nanoseconds: value.getMilliseconds() * 1000000 }
    } else if (Array.isArray(value)) {
      obj[key] = value.map((item: any) =>
        item && typeof item === 'object' ? serializeTimestampsForApi(item) : item
      )
    } else if (typeof value === 'object' && !(value instanceof Date)) {
      obj[key] = serializeTimestampsForApi(value)
    }
  }

  return obj as T
}

/**
 * Returns the current server time by calling the `db/timestamp` API endpoint.
 * The endpoint returns a serialized Firestore Timestamp (`{ seconds, nanoseconds }`)
 * which is converted to a JS `Date` via `coerceToDate()`.
 * Falls back to client time (`new Date()`) on any error.
 *
 * Requires an Angular injection context (called from a service/component) or
 * the global injector fallback set up by the application.
 */
export async function getServerTime(): Promise<Date> {
  try {
    const fss = use<FirestoreService2>(FirestoreService2)
    const api = use<ApiService>(ApiService)
    const res = await fss.toPromise(api.get('db/timestamp'))
    const ts = res?.timestamp ?? res
    const date = coerceToDate(ts)
    if (date instanceof Date && !isNaN(date.getTime())) return date
    console.warn('[getServerTime] Could not parse server time, falling back to client time')
  } catch (e) {
    console.warn('[getServerTime] Failed, falling back to client time', e)
  }
  return new Date()
}
